import React, { useState, useEffect } from 'react'
import { X, Clock, AlertTriangle, CheckCircle, PauseCircle, Archive } from 'lucide-react'
import { teamService } from './teamService'
import { DetailedRosterMember, TrackingStatus, TrackingPauseResolution } from '../../types/team'

interface TrackingStatusModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (message: string) => void
  member: DetailedRosterMember | null
}

export const TrackingStatusModal: React.FC<TrackingStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  member,
}) => {
  const [targetStatus, setTargetStatus] = useState<TrackingStatus>('active')
  const [resolution, setResolution] = useState<TrackingPauseResolution>('keep_pending')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !member) {
      setErrorMsg(null)
      return
    }

    setTargetStatus(member.tracking_status)
    setResolution(member.tracking_pause_resolution || 'keep_pending')
    setErrorMsg(null)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, member, onClose])

  if (!isOpen || !member) return null

  const handleApply = async () => {
    if (targetStatus === member.tracking_status) {
      onClose()
      return
    }

    setIsSubmitting(true)
    setErrorMsg(null)

    try {
      const res = await teamService.setTrackingStatus(
        member.roster_entry_id,
        targetStatus,
        resolution
      )
      if (res.success) {
        onSuccess(
          `Estado de seguimiento de ${member.first_name} ${member.last_name} actualizado a ${
            targetStatus === 'active'
              ? 'Activo'
              : targetStatus === 'paused'
              ? 'Pausado'
              : 'Archivado'
          }.`
        )
        onClose()
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al modificar el seguimiento.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="modal-content"
        style={{ maxWidth: '480px', width: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Clock size={18} style={{ color: 'var(--brand-red)' }} />
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
              Gestionar Ciclo de Seguimiento
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Cerrar modal"
            style={{ padding: '0.25rem' }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          {errorMsg && (
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--status-danger-bg)',
                color: 'var(--status-danger-text)',
                border: '1px solid var(--status-danger-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
              }}
            >
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>{errorMsg}</div>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-main)' }}>
              {member.first_name} {member.last_name} #{member.jersey_number ?? '-'}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              Posición: {member.primary_position} · Unidad: {member.unit}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
            {/* OPCIÓN ACTIVO */}
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.65rem',
                padding: '0.75rem',
                border: targetStatus === 'active' ? '2px solid var(--brand-blue)' : '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: targetStatus === 'active' ? 'var(--surface-subtle)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="tracking_status"
                value="active"
                checked={targetStatus === 'active'}
                onChange={() => setTargetStatus('active')}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600, fontSize: '0.85rem' }}>
                  <CheckCircle size={15} style={{ color: 'var(--status-success-text)' }} />
                  <span>Seguimiento Activo</span>
                </div>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Genera periódicamente las obligaciones de seguimiento semanal y formularios. Reanudar no genera retroactivamente semanas del intervalo pausado.
                </p>
              </div>
            </label>

            {/* OPCIÓN PAUSADO */}
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.65rem',
                padding: '0.75rem',
                border: targetStatus === 'paused' ? '2px solid var(--brand-yellow)' : '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: targetStatus === 'paused' ? 'var(--surface-subtle)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="tracking_status"
                value="paused"
                checked={targetStatus === 'paused'}
                onChange={() => setTargetStatus('paused')}
                style={{ marginTop: '3px' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600, fontSize: '0.85rem' }}>
                  <PauseCircle size={15} style={{ color: 'var(--status-warning-text)' }} />
                  <span>Pausar Seguimiento</span>
                </div>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Suspende temporalmente nuevas obligaciones durante el intervalo de pausa.
                </p>

                {targetStatus === 'paused' && (
                  <div
                    style={{
                      marginTop: '0.65rem',
                      padding: '0.55rem 0.65rem',
                      backgroundColor: 'var(--bg-card)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--surface-border)',
                    }}
                  >
                    <span style={{ display: 'block', fontSize: '0.73rem', fontWeight: 700, marginBottom: '0.35rem' }}>
                      ¿Qué hacer con las obligaciones anteriores?
                    </span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', marginBottom: '0.25rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="pause_resolution"
                        value="keep_pending"
                        checked={resolution === 'keep_pending'}
                        onChange={() => setResolution('keep_pending')}
                      />
                      <span>Mantener pendientes previas abiertas</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="pause_resolution"
                        value="dismiss_pending"
                        checked={resolution === 'dismiss_pending'}
                        onChange={() => setResolution('dismiss_pending')}
                      />
                      <span>Descartar pendientes anteriores (no exigir su entrega)</span>
                    </label>
                  </div>
                )}
              </div>
            </label>

            {/* OPCIÓN ARCHIVADO */}
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.65rem',
                padding: '0.75rem',
                border: targetStatus === 'archived' ? '2px solid var(--text-muted)' : '1px solid var(--surface-border)',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: targetStatus === 'archived' ? 'var(--surface-subtle)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="tracking_status"
                value="archived"
                checked={targetStatus === 'archived'}
                onChange={() => setTargetStatus('archived')}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600, fontSize: '0.85rem' }}>
                  <Archive size={15} style={{ color: 'var(--text-muted)' }} />
                  <span>Archivar Atleta</span>
                </div>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Cesa permanentemente el seguimiento en esta temporada. Conserva intactos todos los registros históricos (snaps, respuestas, estadísticas y convocatorias).
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={handleApply} disabled={isSubmitting}>
            {isSubmitting ? 'Guardando...' : 'Aplicar Estado'}
          </button>
        </div>
      </div>
    </div>
  )
}
